import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  Table,
  Badge,
} from "reactstrap";
import UserHeader from "components/Headers/UserHeader.js";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Notifications = () => {
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    userId: "", // Empty string means broadcast to all users
  });

  const [notifications, setNotifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");

  // Get token from localStorage
  const getToken = () => {
    return localStorage.getItem("token");
  };

  // Auth fetch function
  const authFetch = async (url, options = {}) => {
    const token = getToken();

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...options.headers,
    };

    const response = await fetch(url, { ...options, headers });

    if (response.status === 401 || response.status === 403) {
      localStorage.removeItem("token");
      window.location.href = "/auth/login";
      throw new Error("Authentication failed");
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`,
      );
    }

    return response.json();
  };

  // Fetch notifications
  const fetchNotifications = async (page = 1, userId = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "20",
      });
      if (userId) {
        params.append("userId", userId);
      }

      const data = await authFetch(
        `${process.env.REACT_APP_API_URL}/notifications?${params}`,
      );
      setNotifications(data.notifications || []);
      setPagination({
        page: data.currentPage || page,
        totalPages: data.totalPages || 1,
        total: data.total || 0,
      });
    } catch (error) {
      console.error("Error fetching notifications:", error);
      toast.error(error.message || "Failed to fetch notifications");
    } finally {
      setLoading(false);
    }
  };

  // Fetch users for dropdown
  const fetchUsers = async () => {
    try {
      const data = await authFetch(`${process.env.REACT_APP_API_URL}/users`);
      setUsers(data.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  // Create notification
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.message) {
      toast.error("Title and message are required");
      return;
    }

    try {
      await authFetch(`${process.env.REACT_APP_API_URL}/notifications`, {
        method: "POST",
        body: JSON.stringify({
          title: formData.title,
          message: formData.message,
          userId: formData.userId || null, // null means broadcast
        }),
      });

      toast.success("Notification created successfully!");
      setFormData({
        title: "",
        message: "",
        userId: "",
      });
      fetchNotifications(pagination.page, searchQuery);
    } catch (error) {
      console.error("Error creating notification:", error);
      toast.error(error.message || "Failed to create notification");
    }
  };

  // Delete notification
  const handleDelete = async (notificationId) => {
    if (!window.confirm("Are you sure you want to delete this notification?")) {
      return;
    }

    try {
      await authFetch(
        `${process.env.REACT_APP_API_URL}/notifications?id=${notificationId}`,
        {
          method: "DELETE",
        },
      );

      toast.success("Notification deleted successfully!");
      fetchNotifications(pagination.page, searchQuery);
    } catch (error) {
      console.error("Error deleting notification:", error);
      toast.error(error.message || "Failed to delete notification");
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Get time ago
  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return `${diff} seconds ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    return `${Math.floor(diff / 86400)} days ago`;
  };

  useEffect(() => {
    fetchNotifications();
    fetchUsers();
  }, []);

  return (
    <>
      <UserHeader />
      <Container
        className="mt--7"
        style={{ background: "#251d0c", paddingBottom: "100px" }}
        fluid
      >
        <Row>
          <Col className="order-xl-1" xl="12">
            <Card className="bg-dark">
              <CardBody style={{ background: "black" }}>
                <Form onSubmit={handleSubmit}>
                  <h6
                    className="heading-small text-light mb-4"
                    style={{ fontSize: "1.5rem" }}
                  >
                    Create Notification
                  </h6>
                  <div className="pl-lg-4">
                    <Row>
                      <Col lg="12">
                        <FormGroup>
                          <label
                            style={{ fontSize: "1.4rem" }}
                            className="form-control-label text-light"
                            htmlFor="input-title"
                          >
                            Title (Header)
                          </label>
                          <Input
                            name="title"
                            value={formData.title}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                title: e.target.value,
                              })
                            }
                            placeholder="Enter notification title"
                            required
                            style={{
                              backgroundColor: "#333",
                              color: "#FFB000",
                              fontSize: "1.2rem",
                            }}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="12">
                        <FormGroup>
                          <label
                            style={{ fontSize: "1.4rem" }}
                            className="form-control-label text-light"
                            htmlFor="input-message"
                          >
                            Message (Paragraph)
                          </label>
                          <Input
                            name="message"
                            value={formData.message}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                message: e.target.value,
                              })
                            }
                            placeholder="Enter notification message"
                            type="textarea"
                            rows="4"
                            required
                            style={{
                              backgroundColor: "#333",
                              color: "#FFB000",
                              fontSize: "1.2rem",
                            }}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="12">
                        <FormGroup>
                          <label
                            style={{ fontSize: "1.4rem" }}
                            className="form-control-label text-light"
                            htmlFor="input-userId"
                          >
                            Send To (Leave empty for all users)
                          </label>
                          <Input
                            name="userId"
                            value={formData.userId}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                userId: e.target.value,
                              })
                            }
                            type="select"
                            style={{
                              backgroundColor: "#333",
                              color: "#FFB000",
                              fontSize: "1.2rem",
                            }}
                          >
                            <option value="">All Users (Broadcast)</option>
                            {users.map((user) => (
                              <option key={user._id} value={user._id}>
                                {user.wallet ||
                                  user.email ||
                                  user.name ||
                                  user._id}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Button
                      color="primary"
                      size="lg"
                      style={{
                        backgroundColor: "#FFB000",
                        color: "#222",
                        fontSize: "1.4rem",
                      }}
                    >
                      Send Notification
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col xl="12">
            <Card className="bg-dark">
              <CardBody style={{ background: "black" }}>
                <h6
                  className="heading-small text-light mb-4"
                  style={{ fontSize: "1.5rem" }}
                >
                  Notifications List
                </h6>
                {loading ? (
                  <div className="text-center text-light py-4">Loading...</div>
                ) : notifications.length === 0 ? (
                  <div className="text-center text-light py-4">
                    No notifications found
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table dark hover>
                      <thead>
                        <tr>
                          <th>Title</th>
                          <th>Message</th>
                          <th>Recipient</th>
                          <th>Created</th>
                          <th>Time Ago</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {notifications.map((notification) => (
                          <tr key={notification._id}>
                            <td>
                              <strong>{notification.title}</strong>
                            </td>
                            <td>
                              <div
                                style={{
                                  maxWidth: "300px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {notification.message}
                              </div>
                            </td>
                            <td>
                              {notification.userId ? (
                                <Badge color="info">
                                  {notification.userId?.wallet ||
                                    notification.userId?.email ||
                                    "Specific User"}
                                </Badge>
                              ) : (
                                <Badge color="success">All Users</Badge>
                              )}
                            </td>
                            <td>{formatDate(notification.createdAt)}</td>
                            <td>{getTimeAgo(notification.createdAt)}</td>
                            <td>
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => handleDelete(notification._id)}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}

                {pagination.totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="text-light">
                      Page {pagination.page} of {pagination.totalPages} (Total:{" "}
                      {pagination.total})
                    </div>
                    <div>
                      <Button
                        color="primary"
                        size="sm"
                        disabled={pagination.page === 1}
                        onClick={() =>
                          fetchNotifications(pagination.page - 1, searchQuery)
                        }
                        style={{ marginRight: "10px" }}
                      >
                        Previous
                      </Button>
                      <Button
                        color="primary"
                        size="sm"
                        disabled={pagination.page === pagination.totalPages}
                        onClick={() =>
                          fetchNotifications(pagination.page + 1, searchQuery)
                        }
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <ToastContainer />
    </>
  );
};

export default Notifications;
